import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Award } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "../services/apiClient";

const BrokersDirectory = () => {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrokers();
  }, []);

  const fetchBrokers = async () => {
    try {
      const response = await api.get("/brokers", { auth: false });
      const data = await response.json();
      setBrokers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching brokers:", error);
      setBrokers([]);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
        <span className="text-xs text-muted-foreground ml-1">
          ({rating.toFixed(1)})
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Directorio de Asesores Inmobiliarios
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
              Conecta con asesores inmobiliarios certificados en Monterrey y
              Saltillo
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : brokers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              Aún no hay asesores registrados
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brokers.map((broker, index) => (
              <motion.div
                key={broker.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-background border border-border rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4 mb-4">
                  {broker.image_url ? (
                    <img
                      src={broker.image_url}
                      alt={broker.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Award className="w-8 h-8 text-primary" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3
                      className="font-bold text-lg mb-1"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {broker.name}
                    </h3>
                    {broker.is_verified && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                        ✓ Verificado
                      </span>
                    )}
                  </div>
                </div>

                {broker.rating > 0 && (
                  <div className="mb-3">
                    {renderStars(broker.rating)}
                    <p className="text-xs text-muted-foreground">
                      {broker.review_count} opiniones
                    </p>
                  </div>
                )}

                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {broker.description}
                </p>

                {broker.specialties && broker.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {broker.specialties.slice(0, 3).map((specialty, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-muted text-xs rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                )}

                <div className="text-sm text-muted-foreground space-y-1 mb-4">
                  {broker.years_experience > 0 && (
                    <p>📅 {broker.years_experience} años de experiencia</p>
                  )}
                  {broker.license_number && (
                    <p>📜 Licencia: {broker.license_number}</p>
                  )}
                </div>

                <Button
                  className="w-full"
                  onClick={() => navigate(`/broker/${broker.id}`)}
                >
                  Ver Perfil
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BrokersDirectory;
